import { useState } from "react";
import { AlertTriangle, Mail } from "lucide-react";
import { ApiError, updateOccupant } from "../api/webcheckinApi";
import { useWizardContext } from "./shared";
import { isLikelyOtaAlias } from "../lib/emailHeuristics";
import { otaConfirmedKey } from "../state/wizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Preliminary "confirm your contact email" step.
 *
 * Shown only when the primary email on the reservation looks OTA-forwarded
 * (Booking.com, Expedia, etc). Those aliases route through the OTA and are
 * an unreliable delivery channel — asking the guest for their direct email
 * up-front means every downstream message (arrival instructions, invoice,
 * upsell) reaches them for real.
 *
 * The guest can either accept the address as-is or provide a corrected one;
 * either way we mark the OTA-confirm flag in sessionStorage so a reload
 * doesn't re-nag them. If they change the address we persist it on the
 * primary occupant so operator staff see the corrected value too.
 */
export function ConfirmContactScreen({ token }: { token: string }) {
  const { state, dispatch } = useWizardContext();
  const primary = state.payload!.session.occupants.find((o) => o.role === "primary");
  const currentEmail = primary?.email ?? state.payload!.session.emailAddress;

  const [email, setEmail] = useState(currentEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = email.trim();
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const hasChanged = trimmed.toLowerCase() !== currentEmail.trim().toLowerCase();
  const stillOta = isLikelyOtaAlias(trimmed);

  function markDismissedAndAdvance() {
    try { window.sessionStorage.setItem(otaConfirmedKey(token), "1"); }
    catch { /* Safari private mode; nothing to do */ }
    dispatch({ type: "goto", step: "welcome" });
  }

  async function handleKeep() {
    // Guest confirms the OTA address as their real contact — nothing to
    // persist. We still stamp the dismissal flag so the check-in doesn't
    // start over from this screen after a page refresh.
    markDismissedAndAdvance();
  }

  async function handleSaveChange() {
    if (!looksValid || !primary) return;
    setBusy(true);
    setError(null);
    try {
      await updateOccupant(token, primary.id, { email: trimmed });
      dispatch({
        type: "patchSession",
        patch: {
          occupants: state.payload!.session.occupants.map((o) =>
            o.id === primary.id ? { ...o, email: trimmed } : o
          ),
          emailAddress: trimmed,
        },
      });
      markDismissedAndAdvance();
    } catch (err: unknown) {
      const msg = err instanceof ApiError
        ? (err.message || `Save failed (HTTP ${err.status})`)
        : (err as Error).message;
      setError(msg);
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="screen-h1">Is this the best email for you?</h1>
      <p className="screen-p mt-2">
        Your booking reached us with a <strong>forwarded email address</strong> that goes
        through the booking site. We use email to send arrival details, updates and your
        invoice — please give us the address you check directly so nothing gets lost in the
        forwarding.
      </p>

      <div className="mt-5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">Current email on file</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-amber-800">
            <Mail className="h-3.5 w-3.5 shrink-0" />
            <span className="break-all">{currentEmail}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 space-y-1.5">
        <Label htmlFor="wc-contact-email">Your direct email</Label>
        <Input
          id="wc-contact-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={busy}
        />
        {stillOta && hasChanged && (
          <p className="text-xs text-amber-700">
            That address also looks forwarded — please use the personal one you check
            every day.
          </p>
        )}
        {!looksValid && email.length > 0 && (
          <p className="text-xs text-destructive">Please enter a valid email address.</p>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-8 space-y-2">
        <Button
          className="w-full h-11"
          onClick={hasChanged ? handleSaveChange : handleKeep}
          disabled={busy || (hasChanged && !looksValid)}
        >
          {busy ? "Saving…" : (hasChanged ? "Save and continue" : "Keep this email and continue")}
        </Button>
      </div>
    </div>
  );
}
